import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { isPlaceholder, routingPlan } from "../../lib/vpa";
import { TablePager, useClientPage } from "../../components/sc/Pager";

const INK = "#14101F";
const VIOLET = "#4931E8";
const AMBER = "#b26a00";

function inr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function Account() {
  const { merchant, upiIds, routing, reload } = useOutletContext();
  const [label, setLabel] = useState("");
  const [vpa, setVpa] = useState("");
  const [maxTxns, setMaxTxns] = useState("");
  const [kind, setKind] = useState("merchant"); // merchant (P2M) | personal (P2P)
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [savingShuffle, setSavingShuffle] = useState(false);
  const [savingFallback, setSavingFallback] = useState(false);
  const [stats, setStats] = useState(null);
  const { slice: upiPage, pager: upiPager } = useClientPage(upiIds, 10);

  const autoshuffle = merchant?.autoshuffle !== false; // default ON
  const modeFallback = merchant?.mode_fallback !== false; // default ON
  const activeVpas = upiIds.filter((u) => u.active !== false && !isPlaceholder(u.vpa));
  // P2M and P2P rotate in SEPARATE pools — routing never mixes the two kinds.
  const activeMerchant = activeVpas.filter((u) => (u.kind || "merchant") === "merchant");
  const activePersonal = activeVpas.filter((u) => (u.kind || "merchant") === "personal");
  const defaultVpa = upiIds.find((u) => u.is_default);
  const plan = routingPlan(upiIds, merchant, routing);
  const describe = (p) => {
    const via = p.routes_to && p.next === null ? ` — re-routed via ${p.routes_to.toUpperCase()}` : "";
    if (p.status === "none") return `not set up${via || (p.routes_to ? "" : " — payments fail")}`;
    if (p.status === "disabled") return `all ${p.configured} switched off${via || " — payments will fail"}`;
    const parts = [p.live.length > 1 ? `spreading across ${p.live.length}` : `→ ${p.next}`];
    if (p.standby.length) parts.push(`${p.standby.length} standby`);
    if (p.capped.length) parts.push(`${p.capped.length} maxed today`);
    return parts.join(" · ");
  };
  // UPIs at (MAXED) or near (>=80%) their configured daily cap.
  const nearLimit = upiIds.filter((u) => u.max_txns && u.active !== false && u.today_count >= 0.8 * u.max_txns);

  useEffect(() => {
    api.get("/merchants/me/routing-stats?days=7").then(({ data }) => setStats(data)).catch(() => {});
  }, [upiIds]);

  const toggleShuffle = async () => {
    setSavingShuffle(true);
    try {
      await api.patch("/merchants/me", { autoshuffle: !autoshuffle });
      await reload?.();
      toast.success(!autoshuffle ? "Auto-shuffle on — rotating across VPAs" : "Auto-shuffle off — using your default VPA");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingShuffle(false);
    }
  };

  const toggleFallback = async () => {
    setSavingFallback(true);
    try {
      await api.patch("/merchants/me", { mode_fallback: !modeFallback });
      await reload?.();
      toast.success(!modeFallback ? "Fallback on — payments re-route instead of failing" : "Fallback off — a mode with no active UPI will reject payments");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingFallback(false);
    }
  };

  const addUpi = async () => {
    if (!label.trim() || !vpa.trim()) { toast.error("Label + VPA required"); return; }
    setAdding(true);
    try {
      const body = { label: label.trim(), vpa: vpa.trim().toLowerCase(), kind };
      const cap = parseInt(maxTxns || "0", 10);
      if (cap > 0) body.max_txns = cap;
      await api.post("/merchants/me/upi-ids", body);
      setLabel(""); setVpa(""); setMaxTxns(""); setKind("merchant");
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 2500);
      await reload?.();
      toast.success("UPI ID added ✦");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setAdding(false);
    }
  };

  // Switching off / re-typing / deleting the LAST active VPA of a type empties
  // that mode's pool: payments re-route to the other type (fallback on) or fail
  // outright (fallback off / nothing else active) — confirm first.
  const confirmLastOfKind = (id, patch) => {
    const u = upiIds.find((x) => x.id === id);
    if (!u || u.active === false) return true;
    const k = u.kind || "merchant";
    const leaves = patch === "delete" || patch.active === false || (patch.kind && patch.kind !== k);
    if (!leaves) return true;
    const live = (x) => x.id !== id && x.active !== false && !isPlaceholder(x.vpa);
    if (upiIds.some((x) => live(x) && (x.kind || "merchant") === k)) return true;
    const typeName = k === "personal" ? "Personal (P2P)" : "Merchant (P2M)";
    const otherName = k === "personal" ? "Merchant (P2M)" : "Personal (P2P)";
    const otherLeft = upiIds.some((x) => live(x) && (x.kind || "merchant") !== k);
    const outcome = modeFallback && otherLeft
      ? `${typeName} payments will be re-routed to your ${otherName} UPI IDs until you enable one again.`
      : `${typeName} payment links and API orders will FAIL until you enable another one.`;
    return window.confirm(`${u.vpa} is your only active ${typeName} UPI ID.

${outcome}

Continue?`);
  };

  const updateUpi = async (id, patch) => {
    if (!confirmLastOfKind(id, patch)) return;
    try { await api.patch(`/merchants/me/upi-ids/${id}`, patch); await reload?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const setDefault = async (id) => {
    try { await api.post(`/merchants/me/upi-ids/${id}/default`); await reload?.(); toast.success("Default UPI updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const removeUpi = async (id) => {
    if (!confirmLastOfKind(id, "delete")) return;
    try { await api.delete(`/merchants/me/upi-ids/${id}`); await reload?.(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const badge = (color, bg) => ({ fontFamily: "JetBrains Mono, monospace", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", padding: "2px 7px", borderRadius: 100, border: `1.5px solid ${color}`, color, background: bg, whiteSpace: "nowrap" });
  const toggleStyle = (on, busy) => ({ cursor: busy ? "wait" : "pointer", flex: "none", width: 52, height: 30, borderRadius: 100, background: on ? "#1FBF75" : "#E7E2D6", border: `2.5px solid ${INK}`, position: "relative", transition: "background .15s", padding: 0 });
  const knobStyle = (on) => ({ position: "absolute", top: 2, left: on ? 24 : 2, width: 21, height: 21, borderRadius: "50%", background: "#fff", border: `2px solid ${INK}`, transition: "left .15s" });

  return (
    <div data-testid="account-page" className="ui-page ac-page">

      <div className="ui-grid ui-grid-2" style={{ alignItems: "stretch" }}>
        {/* ── Auto-shuffle routing ── */}
        <section className="ui-card" data-testid="autoshuffle-card">
          <header className="ui-card-head" style={{ flexWrap: "nowrap", alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <span className="ui-eyebrow">Routing</span>
              <h3 className="ui-card-title" style={{ marginTop: 2 }}>Auto-shuffle</h3>
            </div>
            <button data-testid="autoshuffle-toggle" onClick={toggleShuffle} disabled={savingShuffle} title="Toggle auto-shuffle" aria-pressed={autoshuffle}
              style={toggleStyle(autoshuffle, savingShuffle)}>
              <span style={knobStyle(autoshuffle)} />
            </button>
          </header>
          <div className="ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 13, color: "#51495f", margin: 0, lineHeight: 1.55 }}>
              {autoshuffle
                ? <>On — payments are <strong>spread evenly</strong> across every active VPA of each type (the one used least today goes next), skipping any that hit their daily cap. P2M and P2P pools are kept separate.</>
                : <>Off — each payment goes to your primary VPA <strong>of its own type</strong> (the default, then by priority); the next one only takes over when it hits its daily cap.</>}
            </p>
            <div data-testid="routing-now" className="ac-routing-now">
              {[["P2M", plan.p2m], ["P2P", plan.p2p]].map(([k, p]) => (
                <div key={k} style={{ display: "contents" }}>
                  <span style={{ fontWeight: 700, color: "#6a6478", whiteSpace: "nowrap" }}>{k} NOW</span>
                  <span style={{ fontWeight: 700, wordBreak: "break-all", color: p.status === "disabled" ? "#b23b1e" : p.status === "none" ? "#8a84a0" : INK }}>{describe(p)}</span>
                </div>
              ))}
            </div>
            {activeMerchant.length < 2 && activePersonal.length < 2 && (
              <div style={{ background: "#F3F1EC", border: "2px dashed #cfc8b8", borderRadius: 10, padding: "9px 12px", fontSize: 12, color: "#6a6478", lineHeight: 1.5 }}>
                Add a second UPI ID below to spread volume — auto-shuffle only kicks in with 2+ active VPAs.
              </div>
            )}
            {nearLimit.length > 0 && (
              <div data-testid="near-limit-alert" style={{ background: "#FFF4E0", border: `2px solid ${AMBER}`, borderRadius: 10, padding: "9px 12px", fontFamily: "JetBrains Mono, monospace", fontSize: 12, fontWeight: 700, color: AMBER, lineHeight: 1.5 }}>
                ⚠ {nearLimit.length} UPI ID{nearLimit.length === 1 ? "" : "s"} near the daily limit — add another so you keep collecting.
              </div>
            )}
          </div>
        </section>

        {/* ── Mode fallback ── */}
        <section className="ui-card" data-testid="fallback-card">
          <header className="ui-card-head" style={{ flexWrap: "nowrap", alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <span className="ui-eyebrow">Fallback</span>
              <h3 className="ui-card-title" style={{ marginTop: 2 }}>Never refuse a payment</h3>
            </div>
            <button data-testid="fallback-toggle" onClick={toggleFallback} disabled={savingFallback} title="Toggle mode fallback" aria-pressed={modeFallback}
              style={toggleStyle(modeFallback, savingFallback)}>
              <span style={knobStyle(modeFallback)} />
            </button>
          </header>
          <div className="ui-card-pad">
            <p style={{ fontSize: 13, color: "#51495f", margin: 0, lineHeight: 1.55, overflowWrap: "anywhere" }}>
              {modeFallback
                ? <>On — if a P2P order comes in while no <strong>Personal</strong> UPI ID is active, it's collected on your <strong>Merchant</strong> UPI instead (and vice versa), rather than rejected. The money still lands in your own account; the API reports it as <code className="ac-code">payment_mode</code> with the original in <code className="ac-code">requested_payment_mode</code>.</>
                : <>Off — a P2P order with no active Personal UPI ID (or P2M with no Merchant UPI ID) is rejected with a 400.</>}
            </p>
          </div>
        </section>
      </div>

      {/* ── UPI IDs ── */}
      <section className="ui-card" data-testid="upi-card">
        <header className="ui-card-head">
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <h3 className="ui-card-title">Merchant UPI IDs</h3>
            <p className="ui-card-sub" style={{ lineHeight: 1.5 }}>Add every VPA you want to collect on; each has its own <strong>daily limit</strong>. With auto-shuffle off, the <strong>default</strong> takes every payment, then lower <strong>priority</strong> numbers take over as caps are hit.</p>
          </div>
          <span className="ui-badge" style={{ background: "#ECE8FB", color: VIOLET }}>{upiIds.length} added</span>
        </header>

        <div className="ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {upiPage.map((m) => {
            const capped = m.max_txns && m.today_count >= m.max_txns;
            const near = m.max_txns && !capped && m.today_count >= 0.8 * m.max_txns;
            const pct = m.max_txns ? Math.min(100, Math.round((m.today_count / m.max_txns) * 100)) : 0;
            return (
            <div key={m.id} data-testid={`upi-row-${m.id}`} className="ac-upi" style={{ background: m.active === false ? "#F3F1EC" : m.is_default ? "#ECE8FB" : "#FBFAF7", opacity: m.active === false ? 0.7 : 1 }}>
              <div className="ac-upi-top">
                <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span className="ui-eyebrow" style={{ fontSize: 10, overflowWrap: "anywhere" }}>{m.label}{m.is_default ? " · default" : ""}</span>
                    {m.placeholder && <span data-testid={`upi-placeholder-${m.id}`} title="Created at sign-up — not a real UPI ID, never used for payments" style={badge("#b23b1e", "#FBE4DE")}>Placeholder · not used</span>}
                    <span data-testid={`upi-kind-${m.id}`} style={(m.kind || "merchant") === "personal" ? badge("#b26a00", "#FFF4E0") : badge(VIOLET, "#ECE8FB")}>{(m.kind || "merchant") === "personal" ? "Personal · P2P" : "Merchant · P2M"}</span>
                    {capped && <span style={badge("#b23b1e", "#FBE4DE")}>Maxed</span>}
                    {near && <span data-testid={`upi-near-${m.id}`} style={badge(AMBER, "#FFF4E0")}>Near limit</span>}
                  </div>
                  <div className="ui-mono" style={{ fontSize: 14.5, fontWeight: 700, marginTop: 4, wordBreak: "break-all" }}>{m.vpa}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                  <button data-testid={`upi-active-${m.id}`} onClick={() => updateUpi(m.id, { active: m.active === false })} title="Toggle active" aria-pressed={m.active !== false}
                    style={{ cursor: "pointer", width: 46, height: 26, borderRadius: 100, background: m.active === false ? "#E7E2D6" : "#1FBF75", border: `2px solid ${INK}`, position: "relative", padding: 0, flex: "none" }}>
                    <span style={{ position: "absolute", top: 2, left: m.active === false ? 2 : 22, width: 18, height: 18, borderRadius: "50%", background: "#fff", border: `2px solid ${INK}`, transition: "left .15s" }} />
                  </button>
                  {!m.is_default && <button data-testid={`upi-default-${m.id}`} onClick={() => setDefault(m.id)} className="ui-btn ui-btn-sm" style={{ minHeight: 36 }}>Default</button>}
                  {(!m.is_default || m.placeholder) && <button data-testid={`upi-remove-${m.id}`} onClick={() => removeUpi(m.id)} title="Remove UPI ID" aria-label="Remove UPI ID" className="ui-btn ui-btn-danger ui-btn-sm" style={{ minHeight: 36, width: 36, padding: 0, fontSize: 16 }}>×</button>}
                </div>
              </div>
              <div className="ac-upi-controls">
                <label className="ac-mini-field">
                  <span>Priority</span>
                  <input data-testid={`upi-priority-${m.id}`} type="number" min="0" defaultValue={m.priority ?? ""} onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== m.priority) updateUpi(m.id, { priority: v }); }} className="ui-input is-mono" style={{ width: 70, fontWeight: 700 }} />
                </label>
                <label className="ac-mini-field">
                  <span>Daily cap</span>
                  <input data-testid={`upi-cap-${m.id}`} type="number" min="0" placeholder="∞" defaultValue={m.max_txns ?? ""} onBlur={(e) => { const v = parseInt(e.target.value || "0", 10); if (v !== (m.max_txns || 0)) updateUpi(m.id, { max_txns: v }); }} className="ui-input is-mono" style={{ width: 80, fontWeight: 700 }} />
                </label>
                <span className="ui-mono" style={{ fontSize: 11.5, fontWeight: 700, color: capped ? "#b23b1e" : near ? AMBER : "#137a47", alignSelf: "flex-end", paddingBottom: 10 }}>
                  TODAY {m.today_count}{m.max_txns ? `/${m.max_txns}` : ""}{capped ? " · MAXED" : ""}
                </span>
                <button data-testid={`upi-kind-toggle-${m.id}`} onClick={() => updateUpi(m.id, { kind: (m.kind || "merchant") === "personal" ? "merchant" : "personal" })} className="ui-btn ui-btn-sm ac-kind-btn" style={{ minHeight: 36 }}>
                  → {(m.kind || "merchant") === "personal" ? "Make Merchant" : "Make Personal"}
                </button>
              </div>
              {m.max_txns > 0 && (
                <div style={{ marginTop: 12, height: 7, borderRadius: 100, background: "#E7E2D6", overflow: "hidden", border: `1.5px solid ${INK}` }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: capped ? "#b23b1e" : near ? AMBER : "#1FBF75", transition: "width .3s" }} />
                </div>
              )}
            </div>
          );})}
        </div>
        <TablePager testId="upi-pager" {...upiPager} />

        <div className="ui-card-pad" style={{ borderTop: "2px solid var(--ui-line)", display: "flex", flexDirection: "column", gap: 10 }}>
          <span className="ui-eyebrow">Add a UPI ID</span>
          <div className="ac-kind-seg">
            {[["merchant", "Merchant · P2M"], ["personal", "Personal · P2P"]].map(([k, lbl]) => (
              <button key={k} type="button" data-testid={`upi-new-kind-${k}`} onClick={() => setKind(k)} aria-pressed={kind === k}
                style={{ cursor: "pointer", flex: 1, minHeight: 38, padding: "8px 10px", borderRadius: 100, border: "none", background: kind === k ? INK : "transparent", color: kind === k ? "#fff" : INK, fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 13 }}>{lbl}</button>
            ))}
          </div>
          <div className="ac-add-row">
            <label className="ui-field"><span>Label</span>
              <input data-testid="upi-new-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Counter 2)" className="ui-input" />
            </label>
            <label className="ui-field"><span>UPI ID (VPA)</span>
              <input data-testid="upi-new-vpa" value={vpa} onChange={(e) => setVpa(e.target.value)} placeholder="yourstore@bank" className="ui-input is-mono" />
            </label>
            <label className="ui-field"><span>Daily cap</span>
              <input data-testid="upi-new-cap" value={maxTxns} onChange={(e) => setMaxTxns(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="Cap/day" className="ui-input" />
            </label>
          </div>
          <button data-testid="upi-add-btn" onClick={addUpi} disabled={adding} className="ui-btn ui-btn-primary" style={{ width: "100%", fontSize: 15, minHeight: 46 }}>
            {adding ? "Adding…" : "+ Add UPI ID"}
          </button>
          {justAdded && <div style={{ background: "#DFF7E7", border: "2px solid #137a47", borderRadius: 10, padding: "9px 12px", fontFamily: "JetBrains Mono, monospace", fontSize: 12, fontWeight: 700, color: "#137a47" }}>✓ Merchant UPI ID added</div>}
          <p className="ui-muted" style={{ fontSize: 12, margin: "4px 0 0", lineHeight: 1.5 }}>It stays <strong>your</strong> VPA. Priority only matters with auto-shuffle off (lower = used first). Leave the cap blank for unlimited.</p>
        </div>
      </section>

      {/* ── Routing analytics (last 7 days) ── */}
      {stats && stats.total_count > 0 && (
        <section className="ui-card" data-testid="routing-stats-card">
          <header className="ui-card-head">
            <h3 className="ui-card-title">Routing · last 7 days</h3>
            <span className="ui-mono ui-muted" style={{ fontSize: 11.5, fontWeight: 700 }}>{stats.total_count} settled · {inr(stats.total_amount)}</span>
          </header>
          <div className="ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {upiIds.map((u) => {
              const s = stats.by_vpa?.[u.vpa] || { count: 0, amount: 0 };
              const pct = stats.total_count ? Math.round((s.count / stats.total_count) * 100) : 0;
              return (
                <div key={u.id} data-testid={`routing-row-${u.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "4px 10px", marginBottom: 6, flexWrap: "wrap" }}>
                    <span className="ui-mono" style={{ fontSize: 12, fontWeight: 700, wordBreak: "break-all", minWidth: 0 }}>{u.label} · <span style={{ color: "#6a6478" }}>{u.vpa}</span></span>
                    <span className="ui-mono" style={{ fontSize: 11.5, fontWeight: 700, color: "#137a47", flex: "none" }}>{s.count} · {inr(s.amount)} · {pct}%</span>
                  </div>
                  <div style={{ height: 9, borderRadius: 100, background: "#E7E2D6", overflow: "hidden", border: `1.5px solid ${INK}` }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: VIOLET, transition: "width .3s" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <style>{`
        .ac-page { max-width: 1040px; width: 100%; margin: 0 auto; }
        .ac-routing-now { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 6px 12px; font-family: "JetBrains Mono", monospace; font-size: 11.5px; background: var(--ui-surface-2); border: 2px solid var(--ui-line); border-radius: 10px; padding: 10px 12px; }
        .ac-code { font-family: "JetBrains Mono", monospace; font-size: 12px; background: #ECE8FB; padding: 1px 5px; border-radius: 4px; word-break: break-all; }
        .ac-upi { padding: 14px; border: 2px solid ${INK}; border-radius: 14px; min-width: 0; }
        .ac-upi-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .ac-upi-controls { display: flex; align-items: flex-end; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
        .ac-kind-btn { margin-left: auto; }
        .ac-mini-field { display: flex; flex-direction: column; gap: 4px; font-family: "JetBrains Mono", monospace; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6a6478; letter-spacing: .04em; }
        .ac-kind-seg { display: flex; gap: 4px; padding: 4px; border: 2.5px solid ${INK}; border-radius: 100px; background: #fff; box-shadow: 3px 3px 0 ${INK}; }
        .ac-add-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 0.7fr); gap: 10px; }
        @media (max-width: 600px) {
          .ac-add-row { grid-template-columns: minmax(0, 1fr); }
          .ac-kind-btn { margin-left: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
